import { assign, createMachine, Interpreter } from 'xstate'
import { assertEventType } from '~/utility/xstate/assertEvent'
import { UserBooking, UserBookingSummary } from '../user-bookings/schemas'
import { UserNoTokenCanAdd } from './canAddCheckers/UserNoTokenCanAdd'
import { GuestBookingBooking } from './schema'
import { ReviewFormData } from './types'

interface ReviewContext {
  booking: GuestBookingBooking | UserBooking | null
  campsiteSlug: string
  canAdd: boolean
  checking: boolean
  error: string
  errors?: object
  isUser: boolean
  token?: string
  reviewId?: string
  reviewData?: ReviewFormData
}

function getContextInitial() {
  return {
    booking: null,
    campsiteSlug: '',
    canAdd: false,
    checking: false,
    error: '',
    errors: undefined,
    isUser: false,
    token: '',
    reviewId: undefined,
    reviewData: undefined,
  }
}

export type ReviewEvents =
  | CheckUserCanAdd
  | CheckGuestCanAdd
  | SubmitForm
  | { type: 'done.invoke.getUserBookings'; data: UserBookingSummary[] }

export function returnAddReviewStateMachine() {
  return createMachine<ReviewContext, ReviewEvents>(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QEMIQEpgG4EswHcA6HCAGzAGIBhACQFEqBpAfQFUBlO9ZqgQQDlmvACLCA2gAYAuolAAHAPawcAFxwKAdrJAAPRACZ9ARkIBOAGwAOS0YCstq+bsBmSwBoQATwMAWW4WdA10tTCQdzcx8AXyiPVAxsPCIScmp6JmYAcVY6dgAVHgEhUUkZJBBFZTVNbT0EQxMLazsHSydbVw9vBHN9U0JLQJ9nO1NLAHYbUxi4tExcAmIySnZWACEAWQBJAvQ6ADUtugB1Uu1K1XUtcrqGsysbe0cXdy9EcZ9xwnGJSx8-HymIzOYyWGYgeLzJKEADGAAswDCANY4DRQViwMAAJyoyA0vDQFAgmjAxA0WAUSNJkMSi3hiJRaIx2Nx+LQCFRFJhyGqGlKZ3KF15tUQ5lM-Us+klRl+g0sEgiXV85gGQUG+h8+hBETBsQhc1pRHpyNR6MxOLxBIgRJJZIpVMINIWRoRJqZ5tZVo55IU3N5-KMZXkSkuNRuovFAylxllrgV5iVCGBX1sg2c43Mzk1RnMtlMznBTuhxsZZpZlsJ2KxCixhDkpB5ADMawBbR0G52w12l5kWtkQb1cnlXfnSc4h4XhnqRyXS2PyxVveqmHwBILijX50yTQsd4vd02ZACucBUnsJxI0pM5lOpe7pB7Rx9P54HN79I+kAuDVSuIvqPgmEY+i2GEoxGOM+gKvoiYdP4qZZiBwSZjYu4JJ2JaHiesBnhW1pVjWdYNiozZYm2RYPgyWEvnhg6+sOmijkGFQTn+U5ihK0YytYcaLt0tiAWYqaAXmnytOMaFQpRbpllixyqHCeS3hor4UN+LG-mGoB1EC-QgZM27GJ8himImgEqkEgHjK4tgyiBkmGl2VHuti-AKEpVIqXhaljoKrFaboiC6YQ+khJBRjGX0iahIQvyBOYEiBKBK66rM6HQrAR4AEYtqoahojaV52re7bpYsmU5Xlpp0R+jFfr5P6htc2kRpxc48QuCZLoMKp+H8rTis4HEOZ2FW5So+VQBQBG1vWTatqVUlEGNVVojVDF8vVzFCmxLXTm1MYdfGibjBFhB9cMEyZhEtj6CN0IzbAFCrJsOzMHshwnOpO0BXUaYBKEubmJBIQgqZS4RM4Ql-FmcX6NZtgxHqGgKBAcDaBR+DjppzWBQgAC0XXdPj-jimTOaRGJIwSXqmNLOQ2NNf+mqJs4FgDGKwK2foYqandtP3i6zmya+jOTntPiRIQCX2ICkG3Rm9iJlB-RBENaZtM4iWIwLZVCzJz44aLfk48zthfPmGYyvmEXjB0sGCT8MMCaBUqpfqetOTJvbySoinKcbjXi3jMo+BIhCgZMnzApEkwwUuWr+HbEgSBmCXplBCr3dJPbmm5HlgF5-Zi7tIcSGHEep38p1DZ8kqJjYlcpxIpgatrETZ8t2XjZNJe-YgdfncYYxGKYDh2FqJ0at8vxh0CfwymKneEI9fe43Uofh5H1cx3X8fdIh0tqmHVkSPDy-xJAa--pvldRzXsf1915sR4Mnyp6Phjl0jURAA */
      id: 'addReview',
      context: getContextInitial(),
      initial: 'idle',
      on: {
        SUBMIT_REVIEW: {
          target: 'submitting',
          actions: [
            assign((_ctx, event) => {
              assertEventType(event, 'SUBMIT_REVIEW')
              const { campsiteSlug, token, reviewData } = event
              return {
                campsiteSlug,
                token,
                reviewData,
              }
            }),
          ],
        },
        CHECK_USER_CAN_ADD: {
          target: 'checkingUserCanAdd',
          actions: [
            assign({
              campsiteSlug: (_ctx, event) => {
                assertEventType(event, 'CHECK_USER_CAN_ADD')
                return event.campsiteSlug
              },
              checking: (_) => true,
              isUser: (_) => true,
              token: (_ctx, event) => {
                assertEventType(event, 'CHECK_USER_CAN_ADD')
                return event.token
              },
            }),
          ],
        },
        CHECK_GUEST_CAN_ADD: {
          target: 'checkingGuestCanAdd',
          actions: [
            assign({
              checking: (_) => true,
              isUser: (_) => false,
              token: (_ctx, event) => {
                assertEventType(event, 'CHECK_GUEST_CAN_ADD')
                return event.token
              },
            }),
          ],
        },
      },
      states: {
        idle: {
          entry: assign((ctx) => ({
            ...ctx,
            checking: false,
          })),
        },
        checkingUserCanAdd: {
          invoke: {
            src: 'getUserBookings',
            onDone: [{ target: 'idle', actions: ['checkUserNoTokenCanAdd'] }],
            onError: [
              {
                target: 'checkingUserCanAddError',
                actions: [
                  'logError',
                  assign({
                    error: (_, { data }) => data,
                  }),
                ],
              },
            ],
          },
        },
        checkingUserCanAddError: {
          after: {
            3000: 'checkingUserCanAdd',
          },
        },
        checkingGuestCanAdd: {
          invoke: {
            src: 'checkGuestCanAdd',
            onDone: {
              target: 'idle',
              actions: assign({
                booking: (_, { data }) => data.booking,
                canAdd: (_) => true,
                error: (_) => '',
              }),
            },
            onError: [
              // if already added go to added state
              {
                target: 'added',
                cond: (_, { data }) => !!data.reviewId,
                actions: assign({
                  reviewId: (_, { data }) => data.reviewId,
                }),
              },
              // if a user error display
              {
                target: 'errors',
                cond: (_, { data }) => !!data.error,
                actions: [
                  assign({
                    booking: (_) => null,
                    canAdd: (_) => false,
                    error: (_, { data }) => data.error,
                  }),
                ],
              },
              // retry any unexecpted errors and log them
              {
                target: 'checkingGuestCanAddError',
                actions: ['logError'],
              },
            ],
          },
        },
        checkingGuestCanAddError: {
          after: {
            3000: 'checkingGuestCanAdd',
          },
        },
        submitting: {
          invoke: {
            src: 'submitReview',
            onDone: {
              target: 'added',
              actions: assign({
                reviewId: (_, { data }) => data.id,
              }),
            },
            onError: {
              target: 'submittingError',
              actions: [
                'logError',
                assign({
                  error: (_, { data }) => data.error,
                  errors: (_, { data }) => data.errors,
                }),
              ],
            },
          },
        },
        submittingError: {
          always: [
            {
              target: 'idle',
              cond: (context) =>
                !!context.errors && Object.keys(context.errors).length > 0,
            },
          ],
          after: {
            3000: 'submitting',
          },
        },
        errors: {},
        added: {
          type: 'final',
        },
      },
    },
    {
      actions: {
        checkUserNoTokenCanAdd: assign((context, event) => {
          assertEventType(event, 'done.invoke.getUserBookings')
          const { data } = event
          let booking: UserBooking | null = null
          let canAdd = true
          let error = ''
          try {
            booking = new UserNoTokenCanAdd().check(
              data as UserBooking[],
              context.campsiteSlug,
            )
          } catch (err) {
            canAdd = false
            error = (err as Error).message
          }
          return {
            booking,
            canAdd,
            error,
            token: booking ? booking.token : '',
          }
        }),
        logError: (ctx, event: object) => {
          // eslint-disable-next-line no-console
          console.error(event, JSON.stringify(event), JSON.stringify(ctx))
        },
      },
    },
  )
}

export type AddReviewStateMachineInterpreter = Interpreter<
ReviewContext,
  object,
ReviewEvents
>

interface CheckUserCanAdd {
  type: 'CHECK_USER_CAN_ADD'
  campsiteSlug: string
  token?: string
}

interface CheckGuestCanAdd {
  type: 'CHECK_GUEST_CAN_ADD'
  campsiteSlug: string
  token: string
}

export interface SubmitForm {
  type: 'SUBMIT_REVIEW'
  campsiteSlug: string
  reviewData: ReviewFormData
  token?: string
}
